import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { WtValidators } from 'src/app/modules/shared/reactiveValidators/wtValidators';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { AllowedFiltersService } from 'src/app/modules/shared/services/allowed-filters.service';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { CachingService } from 'src/app/modules/shared/services/caching.service';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { IndicatorService } from '../../../services/indicator.service';
import { T } from 'src/assets/i18n/translation-keys';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { Account } from 'src/app/modules/shared/models/account';
import { Employee } from 'src/app/modules/shared/models/employee';
import { Subject, Subscription, debounceTime, forkJoin } from 'rxjs';
import { IndicatorViewModel } from '../../../viewModels/indicatorViewModel';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { IndicatorTrackingDateTypes } from '../../../enums/indicatorTrackingDateTypes';
import { IndicatorUpdateTypes } from '../../../enums/indicatorUpdateTypes';
import { EditableFieldTypes } from 'src/app/modules/shared/enums/editableFieldTypes';
import { IndicatorPriorities } from '../../../enums/indicatorPriorities';
import { FilterUtilities } from 'src/app/modules/shared/utilities/filter.utilities';
import moment from 'moment';
import { FilterDateOptions } from 'src/app/modules/shared/enums/filter/filterDateOptions';
import { FilterItemTypes } from 'src/app/modules/shared/enums/filter/filterItemTypes.enum';
import { ClimatiqService } from '../../../services/climatiq.service';
import { ClimatiqRegionViewModel } from '../../../viewModels/climatiq/climatiqRegionViewModel';
import { ClimatiqSourceViewModel } from '../../../viewModels/climatiq/climatiqSourceViewModel';
import { ClimatiqEmissionFactorSearchViewModel } from '../../../viewModels/climatiq/climatiqEmissionFactorSearchViewModel';
import { ClimatiqIndicatorEstimateParameterSelectorViewModel } from '../../../viewModels/climatiq/climatiqIndicatorEmissionsDataSelectorViewModel';
import { OperationTypes } from 'src/app/modules/shared/enums/operationTypes';
import { IndicatorUpdateExpectedDirections } from '../../../enums/indicatorUpdateExpectedDirections';
import { ClimatiqUnitTypeViewModel } from '../../../viewModels/climatiq/climatiqUnitTypeViewModel';
import { ClimatiqSectorViewModel } from '../../../viewModels/climatiq/climatiqSectorViewModel';
import { UnitsService } from 'src/app/modules/settings/services/units.service';
import { Router } from '@angular/router';

@Component({
    selector: 'app-indicator-add-modal-new',
    templateUrl: './indicator-add-modal-new.component.html',
    styleUrl: './indicator-add-modal-new.component.scss',
    standalone: false
})
export class IndicatorAddModalNewComponent implements OnInit, OnDestroy {
  @Output() submit = new EventEmitter<boolean>();

  private subscriptions: Subscription = new Subscription();

  public readonly T = T;
  public currentAccount: Account;
  public currentEmployee: Employee;

  public localisedDepartment = 'Department';
  public localisedDepartments = 'Departments';
  public localisedDepartmentCategories = 'Department Categories';
  public unitTypesFilters: FilterViewModel[] = [];
  public allowedUnitFilters: FilterViewModel[] = [];
  public unitFilters: FilterViewModel[] = [];
  public isConversionDisabled = false;
  public currentSelectedPriorityFilter: FilterViewModel;
  public loading: boolean = false;
  public indicatorTrackingPeriodFilter: FilterViewModel[] = [];
  public indicator: IndicatorViewModel = new IndicatorViewModel();
  public indicatorForm: UntypedFormGroup;
  public indicatorTemplateForm: UntypedFormGroup;
  public unitOfMeasurementFilters: FilterViewModel[];
  public unitOfMeasurementSelectedFilter: FilterViewModel;
  public expectedDirectionFilters: FilterViewModel[];
  public expectedDirectionSelectedFilter: FilterViewModel;
  public unitTypeSelectedFilter: FilterViewModel;
  public unitSelectedFilter: FilterViewModel;
  public convertionSelectedUnitFilter: FilterViewModel;
  public useCarbonEmissions: boolean = false;
  public filterTypes = FilterTypes;
  public objectTypes = ObjectTypes;
  public indicatorTrackingDateTypes = IndicatorTrackingDateTypes;
  public indicatorUpdateTypes = IndicatorUpdateTypes;
  public editableFieldTypes = EditableFieldTypes;
  public triggerIndicatorErrors: boolean = false;
  public priorityTitles: string[] = [];
  public validationErrors: string[];
  public indicatorCategory: string;

  public today: string;
  public indicatorStartDate: string;
  public indicatorDueDate: string;

  // Emissions Factors CLIMATIQ
  private climatiqEmisionFactorSearchModel: ClimatiqEmissionFactorSearchViewModel = null;
  public climatiqDataIsLoading = false;
  public emissionsFactorIsLoading = false;
  public trackCarbonEmissions = true;
  public climatiqRegions: ClimatiqRegionViewModel[] = [];
  public climatiqSources: ClimatiqSourceViewModel[] = [];
  public climatiqSectors: ClimatiqSectorViewModel[] = [];
  public climatiqUnitTypes: ClimatiqUnitTypeViewModel[] = [];
  public selectedClimatiqRegion: ClimatiqRegionViewModel = null;
  public selectedClimatiqSource: ClimatiqSourceViewModel = null;
  public climatiqIndicatorEmissionsData: ClimatiqIndicatorEstimateParameterSelectorViewModel[] = [];
  public filteredClimaticIndicatorEmissionsData: ClimatiqIndicatorEstimateParameterSelectorViewModel[] = [];

  public matchedClimatiqSector: ClimatiqSectorViewModel = null;
  public matchedClimatiqUnitType: ClimatiqUnitTypeViewModel = null;
  public emissionFactorSearchSubject = new Subject<string>();
  private previousSearchKeywordLength: number = 0;

  public excludedUnitTypeIds: number[] = [];
  public invalidDescription: boolean = false;

  constructor(
    public readonly bsModalRef: BsModalRef,
    public readonly checklistBsModalRef: BsModalRef,
    private readonly router: Router,
    private readonly indicatorService: IndicatorService,
    private readonly authenticationService: AuthenticationService,
    private readonly cachingService: CachingService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly fb: UntypedFormBuilder,
    private readonly localisationService: LocalisationService,
    private readonly allowedFiltersService: AllowedFiltersService,
    private readonly alertService: AlertService,
    private readonly translateService: TranslateService,
    private readonly wtValidators: WtValidators,
    private readonly climatiqService: ClimatiqService,
    private readonly unitsService: UnitsService
  ) {}

  ngOnInit(): void {
    this.currentEmployee = this.authenticationService.getCurrentEmployee();
    this.currentAccount = this.cachingService.GetAccountInfo();
    this.localisedDepartment = this.localisationService.localiseObjectType(ObjectTypes.Department);
    this.localisedDepartments = this.localisationService.localiseObjectType(ObjectTypes.Department, true);
    this.localisedDepartmentCategories = this.localisationService.localiseObjectType(ObjectTypes.Department_Category, true);

    this.today = moment().toISOString();
    this.indicatorStartDate = this.today;
    this.indicatorDueDate = moment().add(5, 'years').toISOString();
    this.initIndicatorForm();
    this.initPriorityFilterTitles();
    this.initIndicatorFilters();
    this.initIndicatorTemplateFilters();
    this.initSubscriptions();
    this.updateIndicatorUpdateType(IndicatorUpdateTypes.Numeric);
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public updateIndicatorCategory(filters: FilterViewModel[]): void {
    this.indicator.filters = filters;
    const indicatorCategoryFilter: FilterViewModel = filters.find((f) => f.filterType === FilterTypes.Indicator_Category);
    this.indicator.indicatorCategoryId = (indicatorCategoryFilter !== undefined)
      ? +indicatorCategoryFilter.filterValue
      : 0;

    this.trackCarbonEmissions = false;
    this.indicator.trackCo2Emissions = false;
    this.indicatorCategory = indicatorCategoryFilter.filterText;

    this.changeDetectorRef.detectChanges();
  }

  public updateIndicatorScope(filters: FilterViewModel[]): void {
    this.indicator.filters = filters;
    const indicatorScopeFilter: FilterViewModel = filters.find((f) => f.filterType === FilterTypes.Sustainability_Scope);
    this.indicator.sustainabilityScope = (indicatorScopeFilter !== undefined)
      ? +indicatorScopeFilter.filterValue
      : 0;

    this.changeDetectorRef.detectChanges();
  }

  public updateGHGReportingCategory(filters: FilterViewModel[]): void {
    this.indicator.filters = filters;
    const indicatorGHGFilter: FilterViewModel = filters.find((f) => f.filterType === FilterTypes.GHG_Reporting_Category);
    this.indicator.GHGReportingCategory = (indicatorGHGFilter !== undefined)
      ? +indicatorGHGFilter.filterValue
      : 0;

    this.changeDetectorRef.detectChanges();
  }

  public updatePillar(filters: FilterViewModel[]): void {
    this.indicator.filters = filters;
    const indicatorPillarFilter: FilterViewModel = filters.find((f) => f.filterType === FilterTypes.Pillar);
    this.indicator.pillar = (indicatorPillarFilter !== undefined)
      ? +indicatorPillarFilter.filterValue
      : 0;

    this.changeDetectorRef.detectChanges();
  }

  public updateReportingDirectives(filters: FilterViewModel[]) {
    this.indicator.filters = filters;
    const indicatorReportingDirectiveFilter: FilterViewModel = filters.find((f) => f.filterType === FilterTypes.Reporting_Directive);
    this.indicator.reportingDirectives = (indicatorReportingDirectiveFilter !== undefined)
      ? +indicatorReportingDirectiveFilter.filterValue
      : 0;
    this.changeDetectorRef.detectChanges();
  }

  public updateIndicatorUpdateType(updateType: IndicatorUpdateTypes): void {
    if(this.indicator.updateType !== updateType) {
      this.indicator.updateType = updateType;

      const indicatorTypeFilter = FilterUtilities.GenerateFilter(FilterTypes.Indicator_Update_Type, updateType, updateType.toString());
      this.indicator.filters = this.indicator.filters.filter((f) => f.filterType !== FilterTypes.Indicator_Update_Type);
      this.indicator.filters.push(indicatorTypeFilter);

      if (this.indicator.updateType === IndicatorUpdateTypes.Pass_Or_Fail) {
        this.indicator.filters = this.indicator.filters.filter((f) => f.filterType !== FilterTypes.Unit_Type && f.filterType !== FilterTypes.Unit);
        this.indicator.defaultUnitId = undefined;
        this.indicator.conversionUnitId = undefined;
        this.indicator.unitTypeId = undefined;
      }

      this.changeDetectorRef.detectChanges();
    }
  }

  public updatedExpectedDirection(filters: FilterViewModel[]): void {
    const expectedDirectionFilter: FilterViewModel = filters.find((f) => f.filterType == FilterTypes.Expected_Direction);
    this.expectedDirectionSelectedFilter = expectedDirectionFilter;

    this.changeDetectorRef.detectChanges();
  }

  public updateIndicatorUnitType(filters: FilterViewModel[]): void {
    if (filters.length > 0) {
      const indicatorUnitTypeFilter: FilterViewModel = filters.find((f) => f.filterType === FilterTypes.Unit_Type);
      this.unitTypeSelectedFilter = indicatorUnitTypeFilter;

      const isDisabled = this.unitTypeSelectedFilter.adittionalItems.find(
        (d) => +d.key === FilterItemTypes.sus_conversionDropdownVisibilityType
      );
      this.isConversionDisabled = isDisabled === undefined ? false : isDisabled.value === '1';

      this.unitFilters = this.allowedUnitFilters.filter((f) => f.relatedObjectId === indicatorUnitTypeFilter.filterValue);
      const defaultUnit = this.unitFilters.find(
        (f) => f.adittionalItems.findIndex((s) => +s.key === FilterItemTypes.sus_unit_default_value && s.value === 'True') > -1
      );

      if (defaultUnit) {
        this.unitSelectedFilter = defaultUnit;
        this.convertionSelectedUnitFilter = defaultUnit;
      } else {
        this.unitSelectedFilter = this.unitFilters.length > 0 ? this.unitFilters[0] : undefined;
        this.convertionSelectedUnitFilter = this.unitFilters.length > 0 ? this.unitFilters[0] : undefined;
      }

      this.indicator.filters = this.indicator.filters.filter(
        (f) => f.filterType !== FilterTypes.Unit_Type && f.filterType !== FilterTypes.Unit
      );
      this.indicator.filters.push(indicatorUnitTypeFilter);

      this.indicator.unitTypeId = +indicatorUnitTypeFilter.filterValue;
      this.indicator.defaultUnitId = +this.unitSelectedFilter.filterValue;
      this.indicator.conversionUnitId = +this.convertionSelectedUnitFilter.filterValue;
      this.useCarbonEmissions = true;
    }
    else {
      this.unitTypeSelectedFilter = undefined;
      this.unitSelectedFilter = undefined;
      this.convertionSelectedUnitFilter = undefined;

      this.indicator.unitTypeId = undefined;
      this.indicator.defaultUnitId = undefined;
      this.indicator.conversionUnitId = undefined;
      this.useCarbonEmissions = false;
      this.setUnitAndUnitTypes();
    }

    this.trackCarbonEmissions = false;
    this.changeDetectorRef.detectChanges();
  }

  public updateIndicatorUnit(filters: FilterViewModel[]): void {
    const unitFilter: FilterViewModel = filters.find((f) => f.filterType === FilterTypes.Unit);
    this.unitSelectedFilter = unitFilter;
    this.indicator.defaultUnitId = unitFilter ? +unitFilter.filterValue : null;
    this.changeDetectorRef.detectChanges();
  }

  public updateConversionIndicatorUnit(filters: FilterViewModel[]): void {
    const unitFilter: FilterViewModel = filters.find((f) => f.filterType === FilterTypes.Unit);
    this.convertionSelectedUnitFilter = unitFilter;
    this.indicator.conversionUnitId = unitFilter ? +unitFilter.filterValue : null;
    this.changeDetectorRef.detectChanges();
  }

  public updateViewModel(filters: FilterViewModel[]): void {
    this.indicator.filters = filters;

    this.changeDetectorRef.detectChanges();
  }

  public onDescriptionChange($event: string): void {
    this.indicatorForm.controls.description.setValue($event);
  }

  public onApply(priorityFilter: FilterViewModel[]): void {
    this.currentSelectedPriorityFilter = priorityFilter.find((f) => f.filterType === FilterTypes.Indicator_Priority);
    this.indicator.filters = priorityFilter;
    this.indicatorForm.patchValue({
      priority: this.currentSelectedPriorityFilter?.filterValue ?? null
    });
  }

  public cancel(): void {
    this.bsModalRef.hide();
  }

  public addCurrentUserDepartment(): void {
    const filters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Department);

    if (filters) {
      const departmentFilter = filters.find(
        (f) => f.filterType === FilterTypes.Department && f.filterValue.toString() === this.currentEmployee.departmentId.toString()
      );
      if (
        departmentFilter &&
        !this.indicator.filters.some(
          (f) => f.filterType === FilterTypes.Department && f.filterValue === this.currentEmployee.departmentId
        )
      ) {
        this.indicator.filters = this.indicator.filters.slice();
        this.indicator.filters.push(departmentFilter);
      }
    }

    this.changeDetectorRef.detectChanges();
  }

  public addCurrentUserAsOwner(): void {
    const predicate = (f: FilterViewModel) => f.filterType === FilterTypes.Owner && f.filterValue === this.currentEmployee.id;

    const filters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Owner);
    if (filters) {
      const empFilter = filters.find(f => predicate(f));
      if (empFilter && !this.indicator.filters.some(f => predicate(f))) {
        this.indicator.filters = this.indicator.filters.slice();
        this.indicator.filters.push(empFilter);
      }
    }

    this.changeDetectorRef.detectChanges();
  }

  public updateStartDate(dateAsIsoString: string): void {
    if (moment(dateAsIsoString).isAfter(moment(this.indicator.dueDate))) {
      this.updateDueDate(moment(dateAsIsoString).add(5, 'years').toISOString());
    }

    this.indicatorStartDate = dateAsIsoString;
    this.setStartDate(dateAsIsoString);
  }

  public updateDueDate(dateAsIsoString: string): void {
    this.indicatorDueDate = dateAsIsoString;
    this.setDueDate(dateAsIsoString);
  }

  public updateTrackingDateType(filters: FilterViewModel[]): void {
    this.indicator.filters = filters;
    const indicatorTrackingDateTypeFilter: FilterViewModel = filters.find(
      (f) => f.filterType === FilterTypes.Indicator_Tracking_Date_Type
    );
    this.indicator.trackingDateType =
      indicatorTrackingDateTypeFilter !== undefined ? +indicatorTrackingDateTypeFilter.filterValue : 0;

    this.changeDetectorRef.detectChanges();
  }

  public onCarbonEmissionsTrackingChanged($event: boolean) {
    this.indicator.trackCo2Emissions = $event;

    if (!this.trackCarbonEmissions) {
      this.getClimatiqData();
    } else {
      this.trackCarbonEmissions = false;
      this.filteredClimaticIndicatorEmissionsData = [];
    }
  }

  public onClimatiqRegionCleared(): void {
    this.selectedClimatiqRegion = null;
    this.getClimatiqEmissionFactorsAndPopulateList();
  }

  public onClimatiqSourceCleared(): void {
    this.selectedClimatiqSource = null;
    this.getClimatiqEmissionFactorsAndPopulateList();
  }

  public onClimatiqRegionChanged(region: ClimatiqRegionViewModel) {
    this.selectedClimatiqRegion = region;
    this.getClimatiqEmissionFactorsAndPopulateList();
  }

  public onClimatiqSourceChanged(source: ClimatiqSourceViewModel) {
    this.selectedClimatiqSource = source;
    this.getClimatiqEmissionFactorsAndPopulateList();
  }

  public onEmissionFactorSelected(emissionFactor: ClimatiqIndicatorEstimateParameterSelectorViewModel) {
    this.indicator.estimateParameter = emissionFactor;
    this.changeDetectorRef.detectChanges();
  }

  public onSave() {
    if (!this.indicatorForm.valid) {
      this.triggerIndicatorErrors = true;
      return;
    }

    this.loading = true;
    if (this.indicator.updateType === IndicatorUpdateTypes.Numeric) {
      const filterValue = this.expectedDirectionSelectedFilter.filterValue as IndicatorUpdateExpectedDirections;
      this.indicator.expectedDirection = filterValue;

      this.indicator.filters.push(
        FilterUtilities.GenerateFilter(
          FilterTypes.Expected_Direction,
          this.expectedDirectionSelectedFilter.filterValue,
          this.expectedDirectionSelectedFilter.filterText
        )
      );
    }

    const item = { ...this.indicator, ...this.indicatorForm.value } as IndicatorViewModel;
    item.active = true;

    //Very important to set the trackCo2Emissions flag here, as the user may have not changed the default radio button.
    item.trackCo2Emissions = this.trackCarbonEmissions;

    if (!item.defaultUnitId) {
      item.defaultUnitId = item.conversionUnitId;
    }

    this.subscriptions.add(
      this.indicatorService.add(item).subscribe((res) => {
        const errors = res.errorList;
        const newIndicator = res.returnModel as IndicatorViewModel;
        if (errors?.length > 0) {
          this.validationErrors = errors;
          this.loading = false;
          void this.alertService.error(errors[0]);
        } else {
          this.allowedFiltersService.refreshFilters(
            OperationTypes.Create,
            [{ id: newIndicator.id, title: newIndicator.title, relatedObjectId: 0 }],
            FilterTypes.Indicator
          );

          this.loading = false;
          this.indicator = res.returnModel as IndicatorViewModel;

          this.submit.next(true);
          void this.alertService.success(
            this.translateService.instant(T.common.item_was_added_successfully, {
              item: this.translateService.instant(T.defaultLocalizations.indicator.one)
            })
          );

          this.closeModal();
          this.router.navigate([`${this.currentAccount.accountUID}/sustainability/indicators/${res.returnModel.id}`]);
          this.changeDetectorRef.detectChanges();
        }
      })
    );
  }

  public closeModal(): void {
    this.bsModalRef.hide();
  }

  private initIndicatorForm(): void {
    this.indicatorForm = this.fb.group({
      title: ['', { validators: [Validators.required, this.wtValidators.textInput(3, 250, WtValidators.RESTRICTED_CHARS.DEFAULT)] }],
      description: [''],
      priority: [0],
      trackingDateValue: ['1', { validators: [Validators.required, this.wtValidators.isNumeric()] }]
    });
  }

  private initPriorityFilterTitles(): void {
    const one_star = this.localisationService.localiseFilterValueByFilterType(
      IndicatorPriorities.One_Star,
      FilterTypes.Indicator_Priority
    );
    const two_stars = this.localisationService.localiseFilterValueByFilterType(
      IndicatorPriorities.Two_Stars,
      FilterTypes.Indicator_Priority
    );
    const three_stars = this.localisationService.localiseFilterValueByFilterType(
      IndicatorPriorities.Three_Stars,
      FilterTypes.Indicator_Priority
    );

    this.priorityTitles = [one_star, two_stars, three_stars];
  }

  private initIndicatorTemplateFilters() {
    this.indicatorTrackingPeriodFilter = [];
    this.indicatorTrackingPeriodFilter.push(
      FilterUtilities.GenerateFilter(FilterTypes.Indicator_Tracking_Date_Type, IndicatorTrackingDateTypes.Years, 'Years')
    );
  }

  private initIndicatorFilters() {
    this.indicator.filters = [];
    this.indicator.filters.push(
      FilterUtilities.GenerateFilter(FilterTypes.Indicator_Tracking_Date_Type, IndicatorTrackingDateTypes.Months, 'Months')
    );
    this.indicator.trackingDateType = IndicatorTrackingDateTypes.Months;
    this.indicator.trackingDateValue = 1;
    this.setStartDate(this.indicatorStartDate);
    this.setDueDate(this.indicatorDueDate);
    this.setExpectedDirectionFilters();
    this.setUnitAndUnitTypes();
  }

  private setStartDate(dateAsIsoString: string): void {
    this.indicator.startDate = dateAsIsoString;
    this.populateDateInFilter(FilterDateOptions.Start_Date, dateAsIsoString);
  }

  private setDueDate(dateAsIsoString: string): void {
    this.indicator.dueDate = dateAsIsoString;
    this.populateDateInFilter(FilterDateOptions.Due_Date, dateAsIsoString);

    if (!this.indicator.startDate) {
      this.indicator.startDate = this.indicator.dueDate;
      this.populateDateInFilter(FilterDateOptions.Start_Date, dateAsIsoString);
    }
  }

  private setExpectedDirectionFilters(): void {
    this.expectedDirectionFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Expected_Direction);
    this.expectedDirectionSelectedFilter = this.expectedDirectionFilters[0];
  }

  private setUnitAndUnitTypes(): void {
    this.allowedUnitFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Unit);
    this.unitTypesFilters = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Unit_Type);

    this.unitsService.getUnitTypeIdsExcludedFromDropdowns().subscribe((ids) => {
      this.excludedUnitTypeIds = ids;
    });
  }


  private populateDateInFilter(filterOption: FilterDateOptions, value: any): void {
    const due = this.indicator.filters.find((f) => f.filterType === FilterTypes.Date && f.dateProperty === filterOption);
    if (!due) {
      this.indicator.filters.push(FilterUtilities.GenerateFilter(FilterTypes.Date, value, value, filterOption));
    } else {
      due.filterValue = value;
    }

    this.changeDetectorRef.detectChanges();
  }

  private updateTrackingDateValue(value: string): void {
    if (!isNaN(+value)) {
      this.indicator.trackingDateValue = +value;
    }
  }

  private updateIndicatorTitle(value: string): void {
    this.indicator.title = value;
  }

  private initSubscriptions(): void {
    const formControls = this.indicatorForm.controls;
    this.subscriptions.add(
      formControls.trackingDateValue.valueChanges.subscribe((value) => this.updateTrackingDateValue(value))
    );
    this.subscriptions.add(
      formControls.title.valueChanges.subscribe((value) => this.updateIndicatorTitle(value))
    );
    this.subscriptions.add(
      this.emissionFactorSearchSubject.pipe(debounceTime(500)).subscribe((search) => {
        this.onSearchEmissionsFactors(search);
      })
    );
  }

  public onSearchEmissionsFactors($event: string) {
    if ($event.length > 2) {
      this.filteredClimaticIndicatorEmissionsData = this.climatiqIndicatorEmissionsData
        .filter(e => e.name.toLowerCase().includes($event.toLowerCase()))
        .slice();
    } else {
      const clearSearchFlag = $event.length < this.previousSearchKeywordLength &&
      this.filteredClimaticIndicatorEmissionsData.length < this.climatiqIndicatorEmissionsData.length;

      if (clearSearchFlag) {
        this.filteredClimaticIndicatorEmissionsData = this.climatiqIndicatorEmissionsData.slice();
      }
    }

    this.previousSearchKeywordLength = $event.length;
  }

  private getClimatiqData(): void {
    this.climatiqDataIsLoading = true;
    forkJoin([
      this.climatiqService.getClimatiqSectors(),
      this.climatiqService.getClimatiqUnitTypes(),
      this.climatiqService.getClimatiqRegions(),
      this.climatiqService.getClimatiqSources(),
    ]).subscribe(([climatiqSector, climatiqUnitTypes, climatiqRegions, climatiqSources]) => {
      this.climatiqSectors = climatiqSector;
      this.climatiqUnitTypes = climatiqUnitTypes;
      this.climatiqRegions = climatiqRegions;
      this.climatiqSources = climatiqSources;
      this.trackCarbonEmissions = true;
      this.matchClimatiqSector();
      this.matchClimatiqUnitType();
      this.getClimatiqEmissionFactorsAndPopulateList();
      this.climatiqDataIsLoading = false;
      this.changeDetectorRef.detectChanges();
    });
  }

  private matchClimatiqSector(): void {
    this.matchedClimatiqSector = this.climatiqSectors.find((s) => s.indicatorCategoryId === this.indicator.indicatorCategoryId);
  }

  private matchClimatiqUnitType(): void {
    this.matchedClimatiqUnitType = this.climatiqUnitTypes.find((u) => u.unitTypeId === this.indicator.unitTypeId);
  }

  private getClimatiqEmissionFactorsAndPopulateList(): void {
    this.emissionsFactorIsLoading = true;
    this.climatiqEmisionFactorSearchModel = {
      region: this.selectedClimatiqRegion?.code,
      sector: this.matchedClimatiqSector?.title,
      unit_type: this.matchedClimatiqUnitType?.title,
      source: this.selectedClimatiqSource?.title
    };

    const flag = this.climatiqEmisionFactorSearchModel.sector && this.climatiqEmisionFactorSearchModel.unit_type;

    if (flag) {
      this.climatiqService.getEmissionFactorSearchResults(this.climatiqEmisionFactorSearchModel).subscribe((data) => {
        this.climatiqIndicatorEmissionsData = data;
        this.filteredClimaticIndicatorEmissionsData = data;
        this.emissionsFactorIsLoading = false;
        this.climatiqDataIsLoading = false;
        this.changeDetectorRef.detectChanges();
      });
    } else {
      this.emissionsFactorIsLoading = false;
      this.climatiqDataIsLoading = false;
    }
  }

  public isIndicatorValid(): boolean {
    return !(
      this.indicatorForm.controls.title.valid &&
      this.indicator.indicatorCategoryId > 0 &&
      this.indicatorForm.controls.priority.valid &&
      this.indicator.updateType &&
      !this.invalidDescription &&
      (this.indicator.updateType !== this.indicatorUpdateTypes.Numeric || this.indicator.conversionUnitId) &&
      (!this.trackCarbonEmissions || this.indicator.estimateParameter)
    );
  }

  public isDescriptionInvalid(disable: boolean) {
    this.invalidDescription = disable;
    this.changeDetectorRef.detectChanges();
  }
}
